import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { callClaude } from '../claude'
import QuestionForm from './QuestionForm'
import { canManageQuestions } from '../permissions'

const STATUS_CONFIG = {
  not_started: { label: 'Not Started', colour: '718096', bg: 'F7FAFC' },
  in_progress:  { label: 'In Progress', colour: 'C9A84C', bg: 'FFF8E7' },
  first_draft:  { label: 'First Draft', colour: '2B6CB0', bg: 'EBF8FF' },
  in_review:    { label: 'In Review',   colour: 'C9A84C', bg: 'FFF8E7' },
  complete:     { label: 'Complete',    colour: '2D6A4F', bg: 'EEF7F2' },
}

function QuestionTracker({ opportunity, profile, orgUsers, onClose }) {
  const [questions, setQuestions] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [editingQuestion, setEditingQuestion] = useState(null)
  const [expandedQuestion, setExpandedQuestion] = useState(null)
  const [error, setError] = useState(null)
  const [draftingId, setDraftingId] = useState(null)
  const [draftQuestion, setDraftQuestion] = useState(null)
  const [draftContent, setDraftContent] = useState('')
  // const [activeTab, setActiveTab] = useState('tracker')

  const [form, setForm] = useState({
    question_number: '',
    question_text: '',
    evaluation_criteria: '',
    word_limit: '',
    page_limit: '',
    weighting: '',
    section: '',
    assigned_to: '',
    status: 'not_started',
    win_theme: '',
    discriminators: '',
    proof_points: '',
    response_structure: '',
    compliance_notes: '',
    graphics_plan: '',
    notes: '',
    response_url: ''
  })

  useEffect(() => {
    loadQuestions()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleGenerateDraft = async (q) => {
    setDraftingId(q.id)
    try {
      const orgProfile = profile?.organisations?.org_profile || {}
      const system = 'You are an expert bid writer specialising in UK Government and Defence procurement. Write a high-quality draft response to an ITT question. Follow APMP best practice: lead with the win theme, evidence every claim, write to the evaluation criteria. Be specific and compelling. Start the response directly with no preamble.'
      const parts = [
        'QUESTION: ' + q.question_text,
        q.evaluation_criteria ? 'EVALUATION CRITERIA: ' + q.evaluation_criteria : '',
        q.word_limit ? 'WORD LIMIT: ' + q.word_limit + ' words' : '',
        q.weighting ? 'WEIGHTING: ' + q.weighting + '%' : '',
        'BUYER: ' + opportunity.buyer,
        opportunity.sector ? 'SECTOR: ' + opportunity.sector : '',
        q.win_theme ? 'WIN THEME: ' + q.win_theme : '',
        q.discriminators ? 'DISCRIMINATORS: ' + q.discriminators : '',
        q.proof_points ? 'PROOF POINTS: ' + q.proof_points : '',
        q.response_structure ? 'RESPONSE STRUCTURE (use as section headers): ' + q.response_structure : '',
        q.compliance_notes ? 'COMPLIANCE REQUIREMENTS: ' + q.compliance_notes : '',
        orgProfile.display_name ? 'ORGANISATION: ' + orgProfile.display_name : '',
        orgProfile.capabilities?.length ? 'CAPABILITIES: ' + orgProfile.capabilities.join(', ') : '',
        orgProfile.certifications?.length ? 'CERTIFICATIONS: ' + orgProfile.certifications.join(', ') : '',
        orgProfile.strengths ? 'STRENGTHS: ' + orgProfile.strengths : '',
        orgProfile.notable_wins ? 'RELEVANT EXPERIENCE: ' + orgProfile.notable_wins : '',
        q.response_structure ? 'Follow the response structure as section headers. Lead with the win theme.' : 'Structure logically. Lead with the win theme.',
      ].filter(Boolean).join('\n')
      const result = await callClaude({ system, prompt: parts, max_tokens: 1500 })
      const draft = result.trim()
      setDraftContent(draft)
      setDraftQuestion(q.id)
      await supabase.from('questions').update({ draft_content: draft }).eq('id', q.id)
      setQuestions(prev => prev.map(item => item.id === q.id ? { ...item, draft_content: draft } : item))
    } catch (err) {
      console.error('Draft error:', err)
    }
    setDraftingId(null)
  }

  const loadQuestions = async () => {
    setLoading(true)
    const { data } = await supabase
      .from('questions')
      .select('*')
      .eq('opportunity_id', opportunity.id)
      .order('question_number')
    setQuestions(data || [])
    setLoading(false)
  }

  const resetForm = () => {
    setForm({
      question_number: '', question_text: '', evaluation_criteria: '',
      word_limit: '', page_limit: '', weighting: '', section: '',
      assigned_to: '', status: 'not_started', win_theme: '',
      discriminators: '', proof_points: '', response_structure: '',
      compliance_notes: '', graphics_plan: '', notes: ''
    })
    setShowAdd(false)
    setEditingQuestion(null)
    setError(null)
  }

  const handleSave = async () => {
    if (!form.question_number.trim() || !form.question_text.trim()) return
    setError(null)

    const payload = {
      question_number: form.question_number.trim(),
      question_text: form.question_text.trim(),
      evaluation_criteria: form.evaluation_criteria || null,
      word_limit: form.word_limit ? parseInt(form.word_limit) : null,
      page_limit: form.page_limit ? parseInt(form.page_limit) : null,
      weighting: form.weighting ? parseFloat(form.weighting) : null,
      section: form.section || null,
      assigned_to: form.assigned_to || null,
      status: form.status,
      win_theme: form.win_theme || null,
      discriminators: form.discriminators || null,
      proof_points: form.proof_points || null,
      response_structure: form.response_structure || null,
      compliance_notes: form.compliance_notes || null,
      graphics_plan: form.graphics_plan || null,
      notes: form.notes || null,
      response_url: form.response_url || null
    }

    if (editingQuestion) {
      const { error } = await supabase
        .from('questions')
        .update(payload)
        .eq('id', editingQuestion.id)
      if (error) { setError(error.message); return }
      setQuestions(prev => prev.map(q =>
        q.id === editingQuestion.id ? { ...q, ...payload } : q
      ))
    } else {
      const { data, error } = await supabase
        .from('questions')
        .insert([{
          ...payload,
          opportunity_id: opportunity.id,
          organisation_id: profile.organisation_id,
          created_by: profile.id
        }])
        .select()
        .single()
      if (error) { setError(error.message); return }

      // Notify assigned user
      if (payload.assigned_to && payload.assigned_to !== profile.id) {
        await supabase.from('notifications').insert([{
          user_id: payload.assigned_to,
          organisation_id: profile.organisation_id,
          opportunity_id: opportunity.id,
          type: 'question_assigned',
          message: `You have been assigned Question ${form.question_number} on ${opportunity.title}`,
          read: false
        }])
      }

      setQuestions(prev => [...prev, data])
    }
    resetForm()
  }

  const handleEdit = (q) => {
    setForm({
      question_number: q.question_number || '',
      question_text: q.question_text || '',
      evaluation_criteria: q.evaluation_criteria || '',
      word_limit: q.word_limit || '',
      page_limit: q.page_limit || '',
      weighting: q.weighting || '',
      section: q.section || '',
      assigned_to: q.assigned_to || '',
      status: q.status || 'not_started',
      win_theme: q.win_theme || '',
      discriminators: q.discriminators || '',
      proof_points: q.proof_points || '',
      response_structure: q.response_structure || '',
      compliance_notes: q.compliance_notes || '',
      graphics_plan: q.graphics_plan || '',
      notes: q.notes || '',
      response_url: q.response_url || ''
    })
    setEditingQuestion(q)
    setShowAdd(true)
    setExpandedQuestion(null)
  }

  const handleDelete = async (id) => {
    await supabase.from('questions').delete().eq('id', id)
    setQuestions(prev => prev.filter(q => q.id !== id))
  }

  const handleStatusChange = async (id, newStatus) => {
    await supabase.from('questions').update({ status: newStatus }).eq('id', id)
    setQuestions(prev => prev.map(q => q.id === id ? { ...q, status: newStatus } : q))
  }

  // Progress calculations
  const total = questions.length
  const complete = questions.filter(q => q.status === 'complete').length
  const inProgress = questions.filter(q => q.status === 'in_progress' || q.status === 'first_draft' || q.status === 'in_review').length
  const notStarted = questions.filter(q => q.status === 'not_started').length
  const progressPct = total > 0 ? Math.round((complete / total) * 100) : 0


  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '24px' }}>

      {/* Header */}
      <div style={{ background: 'var(--green-dark)', padding: '20px 24px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--green-light)', marginBottom: '6px' }}>
              Response Questions
            </div>
            <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem', fontWeight: 600, color: 'var(--white)' }}>
              {opportunity.title}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--silver)', marginTop: '4px' }}>
              {opportunity.buyer}
            </div>
          </div>
          {total > 0 && (
            <div style={{ display: 'flex', gap: '24px', alignItems: 'center', flexShrink: 0 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2.5rem', fontWeight: 700, color: 'var(--white)', lineHeight: 1 }}>
                  {progressPct}%
                </div>
                <div style={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green-light)' }}>
                  Complete
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2.5rem', fontWeight: 700, color: 'var(--white)', lineHeight: 1 }}>
                  {complete}/{total}
                </div>
                <div style={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green-light)' }}>
                  Questions
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Progress bar */}
        {total > 0 && (
          <div style={{ marginTop: '16px' }}>
            <div style={{ height: '6px', background: 'rgba(255,255,255,0.15)', borderRadius: 0 }}>
              <div style={{
                height: '100%', width: `${progressPct}%`,
                background: progressPct === 100 ? 'var(--green-light)' : 'var(--gold)',
                transition: 'width 0.3s'
              }} />
            </div>
            <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
              {[
                { label: 'Not Started', count: notStarted, colour: 'rgba(255,255,255,0.4)' },
                { label: 'In Progress', count: inProgress, colour: 'var(--gold)' },
                { label: 'Complete', count: complete, colour: 'var(--green-light)' },
              ].map(item => item.count > 0 && (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: item.colour, flexShrink: 0 }} />
                  <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)' }}>
                    {item.count} {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {error && <div className="error-msg">{error}</div>}

      {/* Add / Edit form */}
      {showAdd && (
        <QuestionForm
          editingQuestion={editingQuestion}
          form={form}
          setForm={setForm}
          orgUsers={orgUsers}
          onSave={handleSave}
          onCancel={resetForm}
        />
      )}
      {/* Questions list */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--green-dark)' }}>
          {total} Question{total !== 1 ? 's' : ''}
        </div>
        {!showAdd && canManageQuestions(profile) && (
          <button className="btn btn--primary" style={{ padding: '6px 16px', fontSize: '0.8125rem' }} onClick={() => { setShowAdd(true) }}>
            + Add Question
          </button>
        )}
      </div>

      {loading && <p style={{ color: 'var(--slate)' }}>Loading questions...</p>}

      {!loading && questions.length === 0 && (
        <div className="card">
          <p style={{ color: 'var(--slate)', textAlign: 'center', padding: '32px 0' }}>
            No questions yet. Click <strong>+ Add Question</strong> to add your first ITT question.
          </p>
        </div>
      )}

      {questions.map(q => {
        const statusConfig = STATUS_CONFIG[q.status]
        const assignedUser = orgUsers.find(u => u.id === q.assigned_to)
        const isExpanded = expandedQuestion === q.id
        const hasStoryboard = q.win_theme || q.discriminators || q.proof_points || q.response_structure

        return (
          <div
            key={q.id}
            style={{
              background: 'var(--white)', border: '1px solid var(--green-light)',
              marginBottom: '8px', transition: 'box-shadow 0.2s'
            }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(45,106,79,0.08)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
          >
            {/* Question header row */}
            <div
              style={{ padding: '14px 20px', cursor: 'pointer' }}
              onClick={() => setExpandedQuestion(isExpanded ? null : q.id)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--green-dark)', background: 'var(--green-pale)', padding: '2px 8px', flexShrink: 0 }}>
                      {q.question_number}
                    </span>
                    <span style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: `#${statusConfig.colour}`, background: `#${statusConfig.bg}`, padding: '2px 8px' }}>
                      {statusConfig.label}
                    </span>
                    {q.section && (
                      <span style={{ fontSize: '0.625rem', color: 'var(--slate)', background: 'var(--off-white)', padding: '2px 8px', border: '1px solid var(--silver)' }}>
                        {q.section}
                      </span>
                    )}
                    {hasStoryboard && (
                      <span style={{ fontSize: '0.625rem', fontWeight: 600, color: 'var(--green)', background: 'var(--green-pale)', padding: '2px 8px' }}>
                        ✓ Storyboard
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.9375rem', color: 'var(--charcoal)', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isExpanded ? 'normal' : 'nowrap' }}>
                    {q.question_text}
                  </div>
                  <div style={{ display: 'flex', gap: '16px', marginTop: '6px', flexWrap: 'wrap' }}>
                    {q.word_limit && <span style={{ fontSize: '0.75rem', color: 'var(--slate)' }}>📝 {q.word_limit} words</span>}
                    {q.page_limit && <span style={{ fontSize: '0.75rem', color: 'var(--slate)' }}>📄 {q.page_limit} pages</span>}
                    {q.weighting && <span style={{ fontSize: '0.75rem', color: 'var(--slate)' }}>⚖ {q.weighting}%</span>}
                    {assignedUser && <span style={{ fontSize: '0.75rem', color: 'var(--slate)' }}>👤 {assignedUser.full_name || assignedUser.username}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                  <select
                    value={q.status}
                    onChange={e => { e.stopPropagation(); handleStatusChange(q.id, e.target.value) }}
                    onClick={e => e.stopPropagation()}
                    style={{ padding: '4px 8px', border: `1px solid #${statusConfig.colour}`, fontSize: '0.75rem', fontWeight: 600, color: `#${statusConfig.colour}`, background: `#${statusConfig.bg}`, borderRadius: 0, outline: 'none', cursor: 'pointer' }}
                  >
                    {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                      <option key={key} value={key}>{val.label}</option>
                    ))}
                  </select>
                  {q.response_url && (
                    
                    <a href={q.response_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      style={{
                        fontSize: '0.75rem', fontWeight: 600, color: 'var(--green-dark)',
                        textDecoration: 'none', background: 'var(--green-pale)',
                        border: '1px solid var(--green-light)', padding: '4px 10px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      📄 Draft
                    </a>
                  )}
                  <span style={{ fontSize: '0.75rem', color: 'var(--slate)' }}>{isExpanded ? '▲' : '▼'}</span>
                </div>
              </div>
            </div>

            {/* Expanded detail */}
            {isExpanded && (
              <div style={{ padding: '0 20px 16px', borderTop: '1px solid var(--green-light)' }}>
                {q.evaluation_criteria && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green)', marginBottom: '4px' }}>Evaluation Criteria</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--charcoal)', lineHeight: 1.5 }}>{q.evaluation_criteria}</div>
                  </div>
                )}
                {q.win_theme && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green)', marginBottom: '4px' }}>Win Theme</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--charcoal)', lineHeight: 1.5 }}>{q.win_theme}</div>
                  </div>
                )}
                {q.discriminators && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green)', marginBottom: '4px' }}>Discriminators</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--charcoal)', lineHeight: 1.5 }}>{q.discriminators}</div>
                  </div>
                )}
                {q.proof_points && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green)', marginBottom: '4px' }}>Proof Points</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--charcoal)', lineHeight: 1.5 }}>{q.proof_points}</div>
                  </div>
                )}
                {q.response_structure && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green)', marginBottom: '4px' }}>Response Structure</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--charcoal)', lineHeight: 1.5, whiteSpace: 'pre-line' }}>{q.response_structure}</div>
                  </div>
                )}
                {q.compliance_notes && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green)', marginBottom: '4px' }}>Compliance Notes</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--charcoal)', lineHeight: 1.5 }}>{q.compliance_notes}</div>
                  </div>
                )}
                {q.graphics_plan && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green)', marginBottom: '4px' }}>Graphics Plan</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--charcoal)', lineHeight: 1.5 }}>{q.graphics_plan}</div>
                  </div>
                )}
                {q.notes && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green)', marginBottom: '4px' }}>Notes</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--slate)', fontStyle: 'italic', lineHeight: 1.5 }}>{q.notes}</div>
                  </div>
                )}

                {(draftQuestion === q.id ? draftContent : q.draft_content) && (
                  <div style={{ marginTop: '16px', background: 'var(--green-dark)', padding: '16px 20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green-light)' }}>AI Draft Response</div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => navigator.clipboard.writeText(draftContent)} style={{ background: 'none', border: '1px solid var(--green-light)', color: 'var(--green-light)', padding: '2px 10px', fontSize: '0.6875rem', fontWeight: 600, cursor: 'pointer' }}>Copy</button>
                        <button onClick={() => { setDraftQuestion(null); setDraftContent(''); supabase.from('questions').update({ draft_content: null }).eq('id', q.id); setQuestions(prev => prev.map(item => item.id === q.id ? { ...item, draft_content: null } : item)) }} style={{ background: 'none', border: 'none', color: 'var(--silver)', fontSize: '1rem', cursor: 'pointer', padding: '0 4px' }}>×</button>
                      </div>
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--white)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{draftQuestion === q.id ? draftContent : q.draft_content}</div>
                    {q.word_limit && (
                      <div style={{ marginTop: '10px', fontSize: '0.6875rem', color: (draftQuestion === q.id ? draftContent : q.draft_content).split(/\s+/).length > q.word_limit ? '#E67E22' : 'var(--green-light)', fontStyle: 'italic' }}>
                        Draft word count: {(draftQuestion === q.id ? draftContent : q.draft_content).split(/\s+/).length} / {q.word_limit} words
                      </div>
                    )}
                  </div>
                )}
                {/* Action bar */}
                <div style={{ display: 'flex', gap: '8px', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid var(--green-light)' }}>
                  <button
                    onClick={() => canManageQuestions(profile) && handleEdit(q)}
                    disabled={!canManageQuestions(profile)}
                    style={{ background: 'none', border: '1px solid var(--green)', color: 'var(--green)', padding: '4px 14px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
                  >
                    Edit
                  </button>
                  {profile?.organisations?.ai_enabled && (
                    <button
                      onClick={() => handleGenerateDraft(q)}
                      disabled={draftingId === q.id}
                      style={{ background: 'none', border: '1px solid var(--green-dark)', color: 'var(--green-dark)', padding: '4px 14px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
                    >
                      {draftingId === q.id ? '⏳ Drafting...' : '✦ Generate Draft'}
                    </button>
                  )}
                  <button
                    onClick={() => canManageQuestions(profile) && handleDelete(q.id)}
                    disabled={!canManageQuestions(profile)}
                    style={{ background: 'none', border: '1px solid var(--silver)', color: 'var(--slate)', padding: '4px 10px', fontSize: '0.75rem', cursor: 'pointer', marginLeft: 'auto' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--red)'; e.currentTarget.style.color = 'var(--red)' }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--silver)'; e.currentTarget.style.color = 'var(--slate)' }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      })}

      <div className="actions" style={{ marginTop: '16px' }}>
        <button className="btn btn--secondary" onClick={onClose}>← Back to Pipeline</button>
      </div>
    </div>
  )
}

export default QuestionTracker